import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, CheckCircle2, Home, Pencil, Plane, Play, Plus, Trash2, Trophy } from "lucide-react";
import { adminClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { clearMatchCache } from "@/lib/dvStore";
import { matchResult, positionLabel, setScoreError, TEAM_NAME } from "@/lib/dvStats";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const today = () => new Date().toISOString().slice(0, 10);


const setsLine = (m) =>
  [1, 2, 3, 4, 5]
    .filter((n) => m.set_scores?.[String(n)])
    .map((n) => `${m.set_scores[String(n)][0]}-${m.set_scores[String(n)][1]}`)
    .join(" · ");

const resultOf = (m) => {
  let us = 0;
  let them = 0;
  Object.values(m.set_scores || {}).forEach(([a, b]) => {
    if (a > b) us += 1;
    else if (b > a) them += 1;
  });
  return us + them ? `${us} - ${them}` : null;
};

export default function MatchesTab({ onOpenLive, onOpenStats }) {
  const { t } = useT();
  const [matches, setMatches] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await adminClient.get("/datavolley/matches");
    setMatches(data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Failed to load matches"));
  }, []);

  const toggleStatus = async (m) => {
    await adminClient.patch(`/datavolley/matches/${m.id}`, { status: m.status === "live" ? "finished" : "live" });
    await load();
  };

  const remove = async (m) => {
    await adminClient.delete(`/datavolley/matches/${m.id}`);
    clearMatchCache(m.id);
    await load();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="dv-matches-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-black text-xl text-slate-900">{t("dv_matches")}</h2>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="dv-new-match-btn"
          className="dv-btn h-12 px-5 rounded-xl bg-sunset text-white font-bold flex items-center gap-2 shadow-sunset"
        >
          <Plus className="h-5 w-5" /> {t("dv_new_match")}
        </button>
      </div>

      {matches === null ? (
        <p className="text-center text-slate-400 py-10">…</p>
      ) : matches.length === 0 ? (
        <p className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="dv-no-matches">
          {t("dv_no_matches")}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {matches.map((m) => (
            <div key={m.id} className="rounded-2xl bg-white border border-slate-200 shadow-soft p-4 flex flex-col gap-3" data-testid={`dv-match-card-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading font-black text-xl text-slate-900 leading-tight">{TEAM_NAME} <span className="text-slate-400 text-sm font-bold">vs</span></p>
                  <p className="font-heading font-black text-xl text-slate-900 truncate">{m.opponent}</p>
                  <p className="text-sm text-slate-500 flex items-center gap-2 mt-0.5">
                    {m.date}
                    <span className="inline-flex items-center gap-1">
                      {m.venue === "home" ? <Home className="h-3.5 w-3.5" /> : <Plane className="h-3.5 w-3.5" />}
                      {m.venue === "home" ? t("dv_home") : t("dv_away")}
                    </span>
                  </p>
                  {m.competition && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <Trophy className="h-3 w-3" /> {m.competition}
                    </p>
                  )}
                </div>
                <span
                  data-testid={`dv-match-status-${m.id}`}
                  className={`shrink-0 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                    m.status === "live" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {m.status === "live" ? t("dv_live_status") : t("dv_finished")}
                </span>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-3">
                <span>{t("dv_roster")}: {m.roster.length}</span>
                {resultOf(m) && (
                  <span className="font-heading font-black text-slate-900 text-sm" data-testid={`dv-match-result-${m.id}`}>
                    {resultOf(m)} <span className="font-body font-medium text-slate-500 text-xs">({setsLine(m)})</span>
                  </span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-auto">
                <button
                  type="button"
                  onClick={() => setEditing(m)}
                  data-testid={`dv-match-edit-${m.id}`}
                  className="col-span-2 dv-btn h-11 rounded-xl bg-slate-100 text-slate-800 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <Pencil className="h-4 w-4" /> {t("dv_edit")}
                </button>
                <button
                  type="button"
                  onClick={() => onOpenLive(m)}
                  data-testid={`dv-match-open-${m.id}`}
                  className="dv-btn h-12 rounded-xl bg-ocean text-white font-bold flex items-center justify-center gap-2"
                >
                  <Play className="h-4 w-4" /> {t("dv_open")}
                </button>
                <button
                  type="button"
                  onClick={() => onOpenStats(m)}
                  data-testid={`dv-match-stats-${m.id}`}
                  className="dv-btn h-12 rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2"
                >
                  <BarChart3 className="h-4 w-4" /> {t("dv_stats")}
                </button>
                <button
                  type="button"
                  onClick={() => toggleStatus(m)}
                  data-testid={`dv-match-toggle-${m.id}`}
                  className="dv-btn h-11 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" /> {m.status === "live" ? t("dv_mark_finished") : t("dv_reopen")}
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      data-testid={`dv-match-delete-${m.id}`}
                      className="dv-btn h-11 rounded-xl bg-red-50 text-red-600 text-sm font-semibold flex items-center justify-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" /> {t("dv_delete")}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{m.opponent} · {m.date}</AlertDialogTitle>
                      <AlertDialogDescription>{t("dv_delete_match_confirm")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("dv_cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid={`dv-match-delete-confirm-${m.id}`} onClick={() => remove(m)} className="bg-red-600 hover:bg-red-700">
                        {t("dv_delete")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <MatchDialog
        open={open}
        onOpenChange={setOpen}
        onSaved={async (m) => {
          setOpen(false);
          await load();
          onOpenLive(m);
        }}
      />
      <MatchDialog
        open={!!editing}
        match={editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await load();
        }}
      />
    </div>
  );
}

function MatchDialog({ open, onOpenChange, onSaved, match = null }) {
  const { t, lang } = useT();
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState({ opponent: "", date: today(), venue: "home", competition: "" });
  const [scores, setScores] = useState({});
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    adminClient.get("/datavolley/players").then(({ data }) => {
      setPlayers(data);
      setSelected(match ? match.roster.map((p) => p.player_id) : data.map((p) => p.id));
    });
    setForm(
      match
        ? { opponent: match.opponent, date: match.date, venue: match.venue, competition: match.competition || "" }
        : { opponent: "", date: today(), venue: "home", competition: "" }
    );
    setScores(match?.set_scores || {});
  }, [open, match]);

  const setScore = (n, i, v) =>
    setScores((sc) => {
      const cur = sc[n] || ["", ""];
      const next = [...cur];
      next[i] = v;
      return { ...sc, [n]: next };
    });
  const decidedAt = matchResult(
    Object.fromEntries(Object.entries(scores).map(([k, v]) => [k, [Number(v?.[0]) || 0, Number(v?.[1]) || 0]]))
  ).decidedAt;
  const scoreErrors = Object.fromEntries(
    [1, 2, 3, 4, 5]
      .map((n) => {
        const v = scores[String(n)];
        if (!v || v[0] === "" || v[1] === "" || v[0] === undefined || v[1] === undefined) return [n, null];
        return [n, setScoreError(n, Number(v[0]) || 0, Number(v[1]) || 0, t)];
      })
      .filter(([, e]) => e)
  );
  const hasScoreErrors = Object.keys(scoreErrors).length > 0;
  const scoresPayload = () =>
    Object.fromEntries(
      Object.entries(scores)
        .filter(([, v]) => v && (v[0] !== "" || v[1] !== ""))
        .map(([k, v]) => [k, [Number(v[0]) || 0, Number(v[1]) || 0]])
    );

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allPlayers = [
    ...players,
    ...(match ? match.roster.filter((r) => !players.some((p) => p.id === r.player_id)).map((r) => ({ id: r.player_id, ...r })) : []),
  ].sort((a, b) => a.number - b.number);

  const submit = async (e) => {
    e.preventDefault();
    if (hasScoreErrors) {
      toast.error(Object.values(scoreErrors)[0]);
      return;
    }
    setSaving(true);
    try {
      if (match) {
        const current = match.roster.map((p) => p.player_id);
        const { data } = await adminClient.patch(`/datavolley/matches/${match.id}`, {
          ...form,
          set_scores: scoresPayload(),
          add_player_ids: selected.filter((id) => !current.includes(id)),
          remove_player_ids: current.filter((id) => !selected.includes(id)),
        });
        toast.success(`${t("dv_save")}: ${data.opponent}`);
        onSaved(data);
      } else {
        const { data } = await adminClient.post("/datavolley/matches", { ...form, player_ids: selected, set_scores: scoresPayload() });
        toast.success(`${t("dv_new_match")}: ${data.opponent}`);
        onSaved(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{match ? `${t("dv_edit")} · ${match.opponent}` : t("dv_new_match")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="dv-new-match-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_opponent")}</Label>
              <Input required value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} data-testid="dv-match-opponent-input" className="mt-1.5 h-12 rounded-xl text-lg" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_date")}</Label>
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="dv-match-date-input" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_venue")}</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {["home", "away"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm({ ...form, venue: v })}
                    data-testid={`dv-match-venue-${v}`}
                    className={`dv-btn h-12 rounded-xl font-bold border-2 flex items-center justify-center gap-2 ${
                      form.venue === v ? "bg-ocean border-ocean text-white" : "bg-white border-slate-200 text-slate-700"
                    }`}
                  >
                    {v === "home" ? <Home className="h-4 w-4" /> : <Plane className="h-4 w-4" />}
                    {v === "home" ? t("dv_home") : t("dv_away")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_competition")}</Label>
              <Input value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} data-testid="dv-match-competition-input" className="mt-1.5 h-12 rounded-xl" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_set_scores")}</Label>
            <div className="mt-2 grid grid-cols-5 gap-2" data-testid="dv-set-scores">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className={`rounded-xl border p-2 text-center ${scoreErrors[n] ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"} ${decidedAt && n > decidedAt ? "opacity-40 pointer-events-none" : ""}`} data-testid={`dv-score-box-${n}`}>
                  <p className="text-[10px] font-bold text-slate-500 mb-1">SET {n}</p>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      placeholder="–"
                      value={scores[String(n)]?.[0] ?? ""}
                      onChange={(e) => setScore(String(n), 0, e.target.value)}
                      data-testid={`dv-score-${n}-us`}
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white text-center font-heading font-black text-lg text-slate-900"
                    />
                    <span className="text-slate-400 font-bold">-</span>
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      placeholder="–"
                      value={scores[String(n)]?.[1] ?? ""}
                      onChange={(e) => setScore(String(n), 1, e.target.value)}
                      data-testid={`dv-score-${n}-them`}
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white text-center font-heading font-black text-lg text-slate-900"
                    />
                  </div>
                </div>
              ))}
            </div>
            {hasScoreErrors ? (
              <p className="mt-1 text-[11px] font-semibold text-red-600" data-testid="dv-score-error">
                {Object.entries(scoreErrors).map(([n, e]) => `SET ${n}: ${e}`).join(" · ")}
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-slate-500">{t("dv_set_scores_hint")}</p>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("dv_select_players")} ({selected.length}/{allPlayers.length})
            </Label>
            {allPlayers.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">{t("dv_no_players")}</p>
            ) : (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="dv-match-player-picker">
                {allPlayers.map((p) => {
                  const on = selected.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggle(p.id)}
                      data-testid={`dv-match-player-${p.number}`}
                      className={`dv-btn h-14 rounded-xl border-2 px-3 flex items-center gap-3 text-left ${
                        on ? "bg-ocean/10 border-ocean" : "bg-white border-slate-200 opacity-70"
                      }`}
                    >
                      <span className="font-heading font-black text-xl text-slate-900 w-7">{p.number}</span>
                      <span className="min-w-0">
                        <span className="block font-bold text-slate-900 text-sm truncate">{p.name}</span>
                        <span className="block text-[11px] text-slate-500">{positionLabel(p.position, lang)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={saving || selected.length === 0 || hasScoreErrors}
            data-testid="dv-match-create-btn"
            className="dv-btn w-full h-13 py-3.5 rounded-xl bg-sunset text-white font-bold text-lg disabled:opacity-50"
          >
            {match ? t("dv_save") : t("dv_create")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
